import { HoverDisplay } from '@/components/HoverDisplay';
import { HighlightableText } from '@/components/HighlightableText';
import { HighlightableItem } from '@/components/Timeline/TimelineItems/HighlightableItem';
import { DateTimeTooltip } from '@/components/Timeline/TimelineItems/DateTimeTooltip';
import { TimelineAttachments } from '@/components/Timeline/TimelineItems/TimelineAttachments';
import { TimelineCard } from '@/components/Timeline/TimelineItems/TimelineCard';
import { TimelineNoteInput } from '@/components/Timeline/TimelineItems/TimelineNoteInput';
import { TimelineNotes } from '@/components/Timeline/TimelineItems/TimelineNotes';
import { RestrictedContentManager } from '@/pages/PatientTimelinePage/RestrictedContentManager';
import { changeResourceConfidentiality } from '@/services/permissions';
import { CommunicationDirection, ConfidentialityLevel } from '@bariendo/lib/constants';
import { formatPhoneNumber, normalizePhoneNumber } from '@bariendo/lib/phone-validations';
import { formatHumanName, getReferenceString, ProfileResource } from '@medplum/core';
import { ActionIcon, Box, Flex, Group, Menu, Stack, Text, Tooltip } from '@mantine/core';
import { Reference, Resource } from '@medplum/fhirtypes';
import { ResourceAvatar, useMedplum } from '@medplum/react';
import { IconArrowUpRight, IconDotsVertical, IconFilePlus, IconLink, IconLock } from '@tabler/icons-react';
import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { FaxTimelineGroup, TimelineNote } from '../types';

function formatFaxCounterpartyDisplay(label: string): string {
  const digits = normalizePhoneNumber(label);
  if (digits.length >= 10) {
    return formatPhoneNumber(digits);
  }
  return label;
}

interface FaxTimelineItemProps {
  group: FaxTimelineGroup;
  notes?: TimelineNote[];
  highlightRef: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  hasActiveFilters: boolean;
  handleNavigateToItem: (itemReference: string) => void;
  searchTerm?: string;
}

export function FaxTimelineItem({
  group,
  notes = [],
  highlightRef,
  copyDirectLink,
  hasActiveFilters,
  handleNavigateToItem,
  searchTerm,
}: FaxTimelineItemProps): ReactElement {
  const medplum = useMedplum();
  const item = group.items[0];
  const {
    currentUser,
    currentUserPermissions: { canAccessRestrictedContent },
  } = useCurrentUser();
  const [showNoteInput, setShowNoteInput] = useState(false);

  const isInbound = item.direction === CommunicationDirection.INCOMING;
  const title = isInbound ? 'Received fax' : 'Sent fax';
  const lineDisplay = formatFaxCounterpartyDisplay(item.faxCounterparty);

  const changeResourceRestriction = useCallback(
    async (resourceRef: Reference<Resource>, confidentialityLevel: ConfidentialityLevel) => {
      await changeResourceConfidentiality(medplum, {
        resourceRef,
        confidentialityLevel,
      });
    },
    [medplum]
  );

  const messageNotes = notes.filter((note) => getReferenceString(note.linkedTo) === getReferenceString(item.reference));

  const isRestricted = item.confidentiality === ConfidentialityLevel.RESTRICTED;

  const displayName =
    currentUser && item.sender && getReferenceString(item.sender as ProfileResource) === getReferenceString(currentUser)
      ? 'You'
      : item.sender && Array.isArray(item.sender.name) && item.sender.name[0]
        ? formatHumanName(item.sender.name[0])
        : 'Unknown sender';

  return (
    <TimelineCard
      header={
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Text size="md" fw={700}>
              {title}
            </Text>
            <DateTimeTooltip date={group.timestamp} />
          </Group>
        </Group>
      }
    >
      <HighlightableItem
        itemRef={getReferenceString(item.reference) ?? ''}
        highlightRef={highlightRef}
        style={{ width: '100%' }}
      >
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Flex gap="xs" align="center" style={{ minWidth: 0, flex: 1 }}>
              <ResourceAvatar value={item.sender || currentUser} size="sm" style={{ flexShrink: 0 }} />
              <Box style={{ minWidth: 0 }}>
                <Text size="sm" fw={600} truncate>
                  {displayName}
                </Text>
                {lineDisplay ? (
                  <Text size="xs" c="dimmed">
                    <HighlightableText text={lineDisplay} searchTerm={searchTerm ?? ''} />
                  </Text>
                ) : null}
              </Box>
            </Flex>
            <Flex gap="xs" align="center" style={{ flexShrink: 0 }}>
              <HoverDisplay>
                <HoverDisplay.Target>
                  <Menu shadow="md" width={200} position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray" size="sm">
                        <IconDotsVertical size={24} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconFilePlus size={18} color="#246A7A" />}
                        onClick={() => setShowNoteInput(true)}
                        disabled={showNoteInput}
                      >
                        Add sticky note
                      </Menu.Item>
                      {!isRestricted && canAccessRestrictedContent && (
                        <Menu.Item
                          leftSection={<IconLock size={18} color="#246A7A" />}
                          onClick={() => changeResourceRestriction(item.reference, ConfidentialityLevel.RESTRICTED)}
                        >
                          Mark as sensitive
                        </Menu.Item>
                      )}
                      {isRestricted && canAccessRestrictedContent && (
                        <Menu.Item
                          leftSection={<IconLock size={18} color="#246A7A" />}
                          onClick={() => changeResourceRestriction(item.reference, ConfidentialityLevel.NORMAL)}
                        >
                          Mark as not sensitive
                        </Menu.Item>
                      )}
                      <Menu.Item
                        leftSection={<IconLink size={18} color="#246A7A" />}
                        onClick={() => copyDirectLink(getReferenceString(item.reference) ?? '')}
                      >
                        Copy link
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </HoverDisplay.Target>
              </HoverDisplay>
              {isRestricted && (
                <Tooltip label="This fax contains sensitive information">
                  <IconLock style={{ minWidth: '16px' }} size={16} />
                </Tooltip>
              )}
              {hasActiveFilters && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => handleNavigateToItem(getReferenceString(item.reference) ?? '')}
                  title="See full context"
                >
                  <IconArrowUpRight size={16} />
                </ActionIcon>
              )}
            </Flex>
          </Group>

          {item.content ? (
            <Text size="sm">
              <HighlightableText text={item.content} searchTerm={searchTerm ?? ''} />
            </Text>
          ) : null}

          <RestrictedContentManager isRestricted={item.hiddenPayload}>
            <TimelineAttachments
              medplum={medplum}
              mediaAttachments={item.mediaAttachments ?? []}
              pdfAttachments={item.pdfAttachments ?? []}
            />
          </RestrictedContentManager>

          {showNoteInput && (
            <TimelineNoteInput
              timelineItemReference={item.reference}
              onCancel={() => setShowNoteInput(false)}
              onSave={() => setShowNoteInput(false)}
            />
          )}

          {messageNotes.length > 0 && (
            <TimelineNotes
              notes={messageNotes}
              highlightRef={highlightRef}
              copyDirectLink={copyDirectLink}
              hasActiveFilters={hasActiveFilters}
              handleNavigateToItem={handleNavigateToItem}
              searchTerm={searchTerm}
            />
          )}
        </Stack>
      </HighlightableItem>
    </TimelineCard>
  );
}
